import { Fragment, useMemo } from 'react';
import { Button, Dropdown, Skeleton, Tooltip } from 'antd';
import { Link } from 'react-router-dom';

import { getCardScope } from '../../utils/cardScopes';
import { formatCount } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import { createElementResolver, indexCardsById } from '../../utils/cards';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { cardCollectionsPath, childCardsPath } from '../../utils/listLinks';
import { KEBAB, LOCK, PLUS } from '../icons';
import ContextBar from '../common/ContextBar';
import GroupHeader from '../common/GroupHeader';
import { RecordTitle } from '../common/RecordBadge';
import { buildActionMenu } from '../common/actionMenu';
import { cardOwnerOf, normalizeCardId } from './CardVisuals';
import { useCollectionScope } from '../../contexts/CollectionScopeContext';

const resolveActions = (renderer, card) => {
	if (!renderer) return [];
	const next = typeof renderer === 'function' ? renderer(card) : renderer;
	if (!Array.isArray(next)) return [];
	return next.filter(Boolean);
};

const resolveHeaderAction = (renderer, card) => {
	if (!renderer) return null;
	return typeof renderer === 'function' ? renderer(card) : renderer;
};

/**
 * The cards library grid. Either a flat `cards` list or `groups`
 * ([{ key, label, badge?, cards }]) — one grid per group under a GroupHeader,
 * headed by `badge` (a record badge node) when given, else `label`. The
 * element/child resolvers run over EVERY card handed in, whichever shape,
 * so a child in one group still finds its parent's elements in another.
 */
const CardDefinitionGrid = ({
	cards = [],
	groups = null,
	loading = false,
	onCardSelect,
	headerActionPrimary,
	cardActions,
	// An extra foot fact per tile: `card => node` (or a node for all of
	// them). The collection home passes the card's item count in this
	// collection; it leads the meta line, since on that surface it is the
	// fact the reader came for. Falsy = nothing added.
	cardMeta,
	emptyMessage = 'No card definitions yet.',
	headerTitle = 'Cards',
	headerSubtitle,
	showHeader = true,
	showPrimaryAction = true,
	onCreateCard,
	headerActions = null,
	createButtonAriaLabel = 'Create new card',
	// The list route's filter row — the context bar's second row (the cards
	// page passes its search · chips · builder · sort bar).
	toolbar = null,
	// Replaces the default empty block outright (a filtered list's "No cards
	// match" + Clear); `emptyMessage` still fills the default block.
	emptyContent,
}) => {
	const grouped = Array.isArray(groups);
	const allCards = useMemo(() => (
		grouped
			? groups.flatMap(group => (Array.isArray(group?.cards) ? group.cards : []))
			: (Array.isArray(cards) ? cards : [])
	), [grouped, groups, cards]);
	const cardCount = allCards.length;
	const resolvedSubtitle = headerSubtitle ?? (
		`${cardCount} card definition${cardCount === 1 ? '' : 's'}`
	);
	const canRenderPrimaryAction = showPrimaryAction && typeof onCreateCard === 'function';
	const shouldRenderBar = !!(showHeader || canRenderPrimaryAction || headerActions);
	// The platform's one merge rule (utils/cards): a child's re-declaration
	// of an inherited name counts once, as the child defines it — and the
	// element sets a card attaches count too, read from the app-wide cache
	// (optional: the grid also renders outside the provider).
	const elementSetsById = useOptionalCardDefinitions()?.elementSetsById || null;
	const getElementsForCard = useMemo(() => createElementResolver(indexCardsById(allCards), elementSetsById), [allCards, elementSetsById]);

	const childCounts = useMemo(() => {
		const counts = new Map();
		allCards.forEach(card => {
			const id = normalizeCardId(card?._id);
			if (id && !counts.has(id)) counts.set(id, 0);
		});
		allCards.forEach(card => {
			const parentId = normalizeCardId(card?.parentCardId);
			if (!parentId) return;
			counts.set(parentId, (counts.get(parentId) || 0) + 1);
		});
		return counts;
	}, [allCards]);

	const renderTiles = (list) => list.map((card, index) => {
		const cardId = normalizeCardId(card?._id);
		// The API's usage annotation counts children across every readable
		// card; the in-memory count only sees this list.
		const usageChildCount = card?.usage?.childCount;
		const childCount = Number.isFinite(usageChildCount)
			? usageChildCount
			: (cardId ? (childCounts.get(cardId) || 0) : 0);
		return (
			<CardDefinitionPreview
				key={cardId || index}
				card={card}
				meta={resolveHeaderAction(cardMeta, card)}
				// Own elements plus the inherited ones — what the card's form
				// actually renders.
				elementCount={cardId ? getElementsForCard(cardId).length : (card?.elements || []).length}
				childCount={childCount}
				onCardSelect={onCardSelect}
				headerActionPrimary={headerActionPrimary}
				cardActions={cardActions}
			/>
		);
	});

	const skeletonCount = loading ? 6 : 0;
	const skeletons = Array.from({ length: skeletonCount }).map((_, index) => (
		<CardDefinitionSkeleton key={`card-skeleton-${index}`} />
	));

	let gridContent;
	if (!loading && !cardCount) {
		gridContent = emptyContent ?? <div className="card-def-grid__empty">{emptyMessage}</div>;
	} else if (loading) {
		gridContent = <div className="card-def-grid" aria-busy="true">{skeletons}</div>;
	} else if (grouped) {
		// An empty group is skipped outright: a header over nothing is noise.
		gridContent = groups
			.filter(group => Array.isArray(group?.cards) && group.cards.length)
			.map(group => (
				<section key={group.key} className="card-def-group" aria-label={group.label}>
					<GroupHeader
						label={group.badge || group.label}
						count={group.cards.length}
						countLabel="card"
						className={group.badge ? 'card-def-group__header--badge' : ''}
					/>
					<div className="card-def-grid">{renderTiles(group.cards)}</div>
				</section>
			));
	} else {
		gridContent = <div className="card-def-grid">{renderTiles(cards)}</div>;
	}

	const barActions = (headerActions || canRenderPrimaryAction) ? (
		<>
			{headerActions}
			{canRenderPrimaryAction ? (
				<Button
					type="primary"
					icon={<PLUS />}
					onClick={onCreateCard}
					aria-label={createButtonAriaLabel}
				>
					New card
				</Button>
			) : null}
		</>
	) : null;

	return (
		<div className="card-def-grid__section">
			{/* The route's one context bar: title left, the one filled primary
			    far right, the filter row beneath. No chip or breadcrumb — the
			    index is not a record's own surface. */}
			{shouldRenderBar ? (
				<ContextBar
					title={showHeader ? headerTitle : undefined}
					below={showHeader && resolvedSubtitle ? resolvedSubtitle : null}
					actions={barActions}
				>
					{toolbar}
				</ContextBar>
			) : toolbar}

			{gridContent}
		</div>
	);
};

/** A meta fact that is also a door: a quiet link that stops the tile's own open. */
const MetaLink = ({ to, children, stopTile }) => (
	<Link className="card-def-card__meta-link" to={to} onClick={stopTile} onKeyDown={stopTile}>
		{children}
	</Link>
);

const CardDefinitionPreview = ({
	card,
	elementCount,
	childCount,
	// The host's own foot fact, first in the meta line (see `cardMeta`).
	meta,
	onCardSelect,
	headerActionPrimary,
	cardActions,
}) => {
	// The name already says what the card is; only a real description earns
	// the second line.
	const description = typeof card?.description === 'string' ? card.description.trim() : '';
	const isInteractive = typeof onCardSelect === 'function';
	const scope = getCardScope(card);

	// Foot facts, as words and never at zero. A count that names other
	// records is a door to them (the children → the cards list filtered to
	// them; usage → the collections that use the card, or the OWNING
	// collection's badge). The tile itself is a button, so every link stops
	// propagation — click AND keydown — or the tile's open would fire
	// alongside the navigation.
	const stopTile = (event) => event.stopPropagation();
	const usage = card?.usage && typeof card.usage === 'object' ? card.usage : null;
	const ownerCollection = usage && scope === 'collection' ? (usage.ownerCollection || null) : null;
	const usedIn = usage && Array.isArray(usage.collections) ? usage.collections.length : 0;

	// The collection that owns a collection card qualifies its title (the
	// qualified pill's plain form) wherever the grid is not that collection —
	// the cards index across collections; a collection's own card library
	// shows its own cards bare. The usage annotation names a readable owner;
	// one it leaves out is not this reader's to see, so nothing asks. With
	// no annotation, the resolver names it (or not).
	const owner = scope === 'collection'
		? (ownerCollection ? { _id: normalizeId(ownerCollection._id), ...ownerCollection } : (usage ? null : cardOwnerOf(card)))
		: null;
	// The door to that collection: the title's pill is the card's own (one
	// pill, one link), so a quiet "Open Care" in the meta line — only where
	// the grid is not that collection's own library.
	const surfaceCollectionId = useCollectionScope().collectionId;
	const ownerId = ownerCollection ? normalizeId(ownerCollection._id) : null;
	const ownerDoor = ownerId && ownerId !== surfaceCollectionId && ownerCollection.name ? ownerCollection : null;

	const metaParts = [];
	if (meta) metaParts.push(<span key="meta">{meta}</span>);
	const elementsText = formatCount(elementCount, 'element');
	if (elementsText) metaParts.push(<span key="elements">{elementsText}</span>);
	if (scope !== 'collection' && usedIn > 0) {
		metaParts.push(
			<MetaLink key="usage" to={cardCollectionsPath(card)} stopTile={stopTile}>
				{`Used in ${formatCount(usedIn, 'collection')}`}
			</MetaLink>
		);
	}
	if (ownerDoor) {
		metaParts.push(
			<MetaLink key="owner" to={`/collections/${ownerId}/`} stopTile={stopTile}>
				{`Open ${ownerDoor.name}`}
			</MetaLink>
		);
	}
	const childrenText = formatCount(childCount, 'child card');
	if (childrenText) {
		metaParts.push(
			<MetaLink key="children" to={childCardsPath(card)} stopTile={stopTile}>
				{childrenText}
			</MetaLink>
		);
	}

	const handleClick = () => {
		if (isInteractive) {
			onCardSelect(card);
		}
	};

	const handleKeyDown = (event) => {
		if (!isInteractive) return;
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onCardSelect(card);
		}
	};

	const renderHeaderAction = resolveHeaderAction(headerActionPrimary, card);
	const resolvedActions = resolveActions(cardActions, card);

	// Actions arrive as { key, label, group?, glyph?, icon?, danger?, disabled?,
	// onClick(card) } and render in the platform taxonomy.
	const menu = buildActionMenu(resolvedActions.map((action, index) => ({
		...action,
		key: action.key ?? `action-${index}`,
		group: action.group || (action.danger ? 'destructive' : 'record'),
		onClick: () => action.onClick?.(card),
	})));

	const kebabNode = menu ? (
		<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
			<Button
				type="text"
				size="small"
				className="card-def-card__kebab-btn"
				icon={<KEBAB />}
				aria-label="Card actions"
			/>
		</Dropdown>
	) : null;

	const interactiveProps = isInteractive ? {
		role: 'button',
		tabIndex: 0,
		onClick: handleClick,
		onKeyDown: handleKeyDown,
	} : {};

	const cardClasses = ['card-def-card', isInteractive ? 'card-def-card--interactive' : 'card-def-card--static'];

	return (
		<div
			className={cardClasses.join(' ')}
			{...interactiveProps}
		>
			<div className="card-def-card__head">
				<div className="card-def-card__identity">
					<RecordTitle
						kind="card"
						record={card}
						title={card?.name || 'Untitled card'}
						size="md"
						className="card-def-card__title"
						collection={owner}
					/>
					{/* Global cards are told apart by one quiet glyph, not a
					    footer pill. */}
					{/* A retired one (KPR-280) says so on the same glyph: still
					    working where it is held, no longer offered to others. */}
					{scope === 'global' ? (
						<Tooltip title={card?.retiredAt ? 'Global card, retired from the Card Library' : 'Global card'}>
							<span className="card-def-card__lock" role="img" aria-label={card?.retiredAt ? 'Global card, retired' : 'Global card'}>
								<LOCK />
							</span>
						</Tooltip>
					) : null}
				</div>
				{(renderHeaderAction || kebabNode) ? (
					<div
						className="card-def-card__actions"
						onClick={stopTile}
						onMouseDown={stopTile}
						onKeyDown={stopTile}
						role="presentation"
					>
						{renderHeaderAction}
						{kebabNode}
					</div>
				) : null}
			</div>

			{description ? (
				<p className="card-def-card__description">{description}</p>
			) : null}

			{metaParts.length ? (
				<div className="card-def-card__meta meta-line">
					{metaParts.map((part, index) => (
						<Fragment key={part.key}>
							{index > 0 ? <span className="card-def-card__meta-dot" aria-hidden="true"> · </span> : null}
							{part}
						</Fragment>
					))}
				</div>
			) : null}
		</div>
	);
};

const CardDefinitionSkeleton = () => (
	<div className="card-def-card card-def-card--skeleton" aria-hidden="true">
		<div className="card-def-card__head">
			<div className="card-def-card__identity">
				<Skeleton.Avatar active size={24} shape="square" />
				<Skeleton.Input active size="small" style={{ width: 140 }} />
			</div>
		</div>
		<div className="card-def-card__description">
			<Skeleton active title={false} paragraph={{ rows: 2, width: ['100%', '70%'] }} />
		</div>
		<div className="card-def-card__meta">
			<Skeleton.Input active size="small" style={{ width: 120 }} />
		</div>
	</div>
);

export default CardDefinitionGrid;
